import React from 'react';
import { motion } from 'framer-motion';
import {
  BeakerIcon,
  RocketLaunchIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { useFinancialModel } from './FinancialModelContext';

const IRSlide10ThreeYearRoadmap: React.FC = () => {
  const { calculations } = useFinancialModel();
  const { yearSummaries, totalFunding } = calculations;

  const formatCurrency = (value: number, short = true) => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (short) {
      if (absValue >= 1000000) return `${sign}$${(absValue / 1000000).toFixed(2)}M`;
      if (absValue >= 1000) return `${sign}$${(absValue / 1000).toFixed(0)}K`;
    }
    return `${sign}$${absValue.toLocaleString()}`;
  };

  const formatNumber = (value: number) => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  };

  const yearConfig = [
    {
      icon: BeakerIcon,
      color: '#f59e0b',
      title: 'Foundation & Validation',
      keyMilestones: [
        'Product launch Q3',
        'Paid pilot schools secured',
        'Initial B2C acquisition',
        'CAC optimisation',
      ],
    },
    {
      icon: RocketLaunchIcon,
      color: '#0ea5e9',
      title: 'Revenue & Scaling',
      keyMilestones: [
        '33% quarterly B2C growth',
        'Activation of year 1 sales activity',
        'Breakeven trajectory',
        'SEO content ranking',
      ],
    },
    {
      icon: TrophyIcon,
      color: '#10b981',
      title: 'Optimisation & Share Growth',
      keyMilestones: [
        '5% of independent schools',
        '1% of total student market',
        'WA/SA market entry',
        'Extension of school value proposition',
      ],
    },
  ];

  return (
    <div className="flex flex-col h-full relative overflow-hidden">
      {/* Atmospheric background - matching slides 2 & 3 */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 120% 80% at 50% 120%, rgba(139, 92, 246, 0.12) 0%, transparent 50%), radial-gradient(ellipse 100% 60% at 20% 20%, rgba(245, 158, 11, 0.08) 0%, transparent 40%), radial-gradient(ellipse 100% 60% at 80% 20%, rgba(16, 185, 129, 0.08) 0%, transparent 40%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-16 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <h2 className="text-4xl font-bold text-white tracking-tight mb-1">
            3-Year Financial Roadmap
          </h2>
          <p className="text-lg text-[#94a3b8]">
            From validation to scale — based on {formatCurrency(totalFunding, true)} total funding across Tranche 1 & 2
          </p>
        </motion.div>

        {/* Three columns */}
        <div className="flex-1 grid grid-cols-3 gap-6 min-h-0">
          {yearSummaries.map((year, index) => {
            const config = yearConfig[index];
            const Icon = config.icon;

            return (
              <motion.div
                key={year.year}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="relative"
              >
                {/* Glow accent */}
                <div
                  className="absolute -left-3 top-0 bottom-0 w-1 rounded-full"
                  style={{ background: `linear-gradient(to bottom, ${config.color}, ${config.color}50, transparent)` }}
                />
                <div
                  className="absolute -left-3 top-0 w-1 h-20 rounded-full blur-md"
                  style={{ background: config.color }}
                />

                <div className="pl-5 h-full flex flex-col overflow-hidden">
                  {/* Year Header */}
                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-1">
                      <Icon className="w-8 h-8" style={{ color: config.color }} />
                      <div>
                        <div className="text-base tracking-widest uppercase" style={{ color: config.color }}>
                          {year.period}
                        </div>
                        <h3 className="text-3xl font-bold text-white">{year.year}</h3>
                      </div>
                    </div>
                    <p className="text-lg text-[#94a3b8]">{config.title}</p>
                  </div>

                  {/* B2B Section */}
                  <div
                    className="p-5 rounded-lg mb-3"
                    style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                  >
                    <div className="text-base text-[#10b981] uppercase tracking-wider mb-2">B2B School Licences</div>
                    <div className="grid grid-cols-2 gap-1 text-base mb-1">
                      <div>
                        <span className="text-[#64748b]">Schools: </span>
                        <span className="text-white font-medium">
                          {year.b2bSchoolsStart} → {year.b2bSchoolsEnd}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#64748b]">Fee: </span>
                        <span className="text-white font-medium">
                          {year.b2bFeePerStudent > 0 ? `$${year.b2bFeePerStudent}/mo` : 'Pilots'}
                        </span>
                      </div>
                    </div>
                    <div className="text-base text-[#64748b] mb-1">
                      {formatNumber(year.b2bStudentsEnd)} student licences
                    </div>
                    <div className="text-2xl font-bold text-[#10b981]">
                      {formatCurrency(year.b2bRevenue)}
                    </div>
                  </div>

                  {/* B2C Section */}
                  <div
                    className="p-5 rounded-lg mb-3"
                    style={{ background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.2)' }}
                  >
                    <div className="text-base text-[#0ea5e9] uppercase tracking-wider mb-2">B2C Subscriptions</div>
                    <div className="grid grid-cols-2 gap-1 text-base mb-1">
                      <div>
                        <span className="text-[#64748b]">Subs: </span>
                        <span className="text-white font-medium">
                          {formatNumber(year.b2cSubsStart)} → {formatNumber(year.b2cSubsEnd)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#64748b]">Fee: </span>
                        <span className="text-white font-medium">${year.b2cMonthlyFee}/mo</span>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-[#0ea5e9]">
                      {formatCurrency(year.b2cRevenue)}
                    </div>
                  </div>

                  {/* Key Milestones */}
                  <div className="mb-3 flex-1 min-h-0 overflow-hidden">
                    <div className="text-base text-[#64748b] uppercase tracking-wider mb-2">Key Milestones</div>
                    <div className="space-y-2">
                      {config.keyMilestones.map((milestone) => (
                        <div key={milestone} className="flex items-start gap-2">
                          <div
                            className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                            style={{ background: config.color }}
                          />
                          <span className="text-base text-[#e2e8f0]">{milestone}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom Summary */}
                  <div
                    className="p-5 rounded-lg"
                    style={{ background: `${config.color}15`, borderLeft: `3px solid ${config.color}` }}
                  >
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div>
                        <div className="text-base text-[#64748b]">Total Revenue</div>
                        <div className="text-2xl font-bold" style={{ color: config.color }}>
                          {formatCurrency(year.totalRevenue)}
                        </div>
                      </div>
                      <div>
                        <div className="text-base text-[#64748b]">Net Revenue</div>
                        <div className={`text-2xl font-bold ${year.netRevenue < 0 ? 'text-[#ef4444]' : 'text-[#10b981]'}`}>
                          {formatCurrency(year.netRevenue)}
                        </div>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-white/10">
                      <div className="text-base text-[#64748b]">Cash Position</div>
                      <div className={`text-xl font-bold ${year.cumulativePosition < 0 ? 'text-[#f59e0b]' : 'text-[#10b981]'}`}>
                        {formatCurrency(year.cumulativePosition)}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-4 pt-4 border-t border-white/10 flex-shrink-0"
        >
          <div className="flex justify-center items-center gap-8">
            <div className="text-center">
              <div className="text-base text-[#64748b]">3-Year Total Revenue</div>
              <div className="text-3xl font-bold text-white">{formatCurrency(calculations.totalRevenue)}</div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div className="text-center">
              <div className="text-base text-[#64748b]">Year 3 Net Revenue</div>
              <div className="text-3xl font-bold text-[#10b981]">{formatCurrency(calculations.y3Total)}</div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div className="text-center">
              <div className="text-base text-[#64748b]">Final Cash Position</div>
              <div className={`text-3xl font-bold ${calculations.finalCash < 0 ? 'text-[#ef4444]' : 'text-[#10b981]'}`}>
                {formatCurrency(calculations.finalCash)}
              </div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <span className="text-base text-[#64748b] bg-[#1e293b] px-3 py-1 rounded-full">
              Values from Slide 9
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IRSlide10ThreeYearRoadmap;
